<template>
  <Slide class="products-slide">
    <div class="products-header">
      <div class="products-title">
        <SlideTitle type="with-menu" :items="slideMenuItems">{{ getCurrentStatisticTypeLabel }}</SlideTitle>
        <p class="products-description">{{ getCurrentStatisticTypeDescription }}</p>
      </div>
      <div class="orders-filters">
        <SlideFilters
          :products="productFilterOptions"
          :selected-product="{ id: getSelectedProductFilter || 'all' }"
          :partners="partnerFilterOptions"
          :partners-disable="!isMainPartner || isBankPartner"
          :selected-partners="{ id: getSelectedPartnersFilter || 'all' }"
          :period-items="periodFilterOptions"
          :period-selected-item="{ id: getSelectedPeriodMode || 'current_month' }"
          :period="{ range: true, mode: getSelectedPeriodMode, ...periodFilterLimits, ...calendarConfig, selectedPeriod: { startDate: getActiveFilters.period[0] || null, endDate: getActiveFilters.period[1] || null }}"
          :options="{ eventMap: periodCalendarEventMap }"
        />
      </div>
    </div>
    <section class="orders-content">
      <div class="products-chart">
        <DonutChartSkeleton :is-loading="!isProductsDataReady" style="height: 600px">
          <Chart title="За календарний рік" :options="getCurrentChartOptions" height="512px" />
          <EmptyChart v-if="isChartEmpty" class="empty-products-chart">
            <p class="message-title">{{ getEmptyProductsChartMessage }}</p>
          </EmptyChart>
        </DonutChartSkeleton>
      </div>
    </section>
  </Slide>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  periodFilterLimits,
  PRODUCTS_PARTNER_FILTER_EVENTS,
  PRODUCTS_PERIOD_FILTER_EVENTS,
  PRODUCTS_PRODUCT_FILTER_EVENTS,
  PRODUCTS_STATS_EVENTS,
  productsPartnerFilterOptions,
  productsPeriodCalendarEventMap,
  productsPeriodFilterOptions,
  productsProductFilterOptions,
  productsSlideMenuItems,
  productsStatisticTypes,
} from '@/features/order-statistics/config';
import { filterCalculator, productsPartnerFilter, productsPartnerSelfFilter } from '@/features/order-statistics/lib';
import { balanceFormatter } from '@/shared/lib/balance';
import { calendarConfig } from '@/shared/lib/calendar';
import { expirationText } from '@/shared/lib/string';
import { Chart, DonutChartSkeleton, EmptyChart } from '@/shared/ui/chart';
import { POPUP_EVENTS } from '@/shared/ui/popup';
import { Slide, SlideFilters, SlideTitle } from '@/shared/ui/slider';

export default {
  data() {
    return {
      slideMenuItems: productsSlideMenuItems,
      productFilterOptions: productsProductFilterOptions,
      partnerFilterOptions: productsPartnerFilterOptions,
      periodFilterOptions: productsPeriodFilterOptions,
      periodFilterLimits,
      selectedStatisticType: productsStatisticTypes[0].value,
      periodCalendarEventMap: productsPeriodCalendarEventMap,
    };
  },
  created() {
    this._eventHandlers = {
      onStatsAmount: () => this.setSelectedStatisticType('amount'),
      onStatsCount: () => this.setSelectedStatisticType('count'),
      onStatsEarnings: () => this.setSelectedStatisticType('earnings'),
      onProductAll: () => this.setSelectedFilter('product', null, false),
      onProductOsago: () => this.setSelectedFilter('product', 'osago', true),
      onProductGreenCard: () => this.setSelectedFilter('product', 'green_card', true),
      onProductTourism: () => this.setSelectedFilter('product', 'tourism', true),
      onProductHome: () => this.setSelectedFilter('product', 'home', true),
      onProductExternal: () => this.setSelectedFilter('product', 'manual', true),
      onProductMiniKasko: () => this.setSelectedFilter('product', 'miniKasko', true),
      onProductPets: () => this.setSelectedFilter('product', 'pets', true),
      onPartnerAll: () => this.setSelectedFilter('partner', null, false),
      onPeriodCurrentMonth: () => { this.setPeriodMode(null); this.setSelectedFilter('period', [null, null], false); },
      onPeriodDay: () => this.setPeriodMode('days'),
      onPeriodWeek: () => this.setPeriodMode('weeks'),
      onPeriodMonth: () => this.setPeriodMode('months'),
      onPeriodDatesRange: (event) => this.setSelectedFilter('period', [event.startDate, event.endDate]),
    };

    this.fetchProductsStatistics();

    this.$eventBus.$on(PRODUCTS_STATS_EVENTS.PRODUCTS_STATS_AMOUNT, this._eventHandlers.onStatsAmount);
    this.$eventBus.$on(PRODUCTS_STATS_EVENTS.PRODUCTS_STATS_COUNT, this._eventHandlers.onStatsCount);
    this.$eventBus.$on(PRODUCTS_STATS_EVENTS.PRODUCTS_STATS_EARNINGS, this._eventHandlers.onStatsEarnings);
    this.$eventBus.$on(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_ALL, this._eventHandlers.onProductAll);
    this.$eventBus.$on(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_OSAGO, this._eventHandlers.onProductOsago);
    this.$eventBus.$on(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_GREEN_CARD, this._eventHandlers.onProductGreenCard);
    this.$eventBus.$on(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_TOURISM, this._eventHandlers.onProductTourism);
    this.$eventBus.$on(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_HOME, this._eventHandlers.onProductHome);
    this.$eventBus.$on(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_EXTERNAL, this._eventHandlers.onProductExternal);
    this.$eventBus.$on(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_MINI_KASKO, this._eventHandlers.onProductMiniKasko);
    this.$eventBus.$on(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_PETS, this._eventHandlers.onProductPets);
    this.$eventBus.$on(PRODUCTS_PARTNER_FILTER_EVENTS.PRODUCTS_PARTNER_FILTER_ALL, this._eventHandlers.onPartnerAll);
    this.$eventBus.$on(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_CURRENT_MONTH, this._eventHandlers.onPeriodCurrentMonth);
    this.$eventBus.$on(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_DAY, this._eventHandlers.onPeriodDay);
    this.$eventBus.$on(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_WEEK, this._eventHandlers.onPeriodWeek);
    this.$eventBus.$on(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_MONTH, this._eventHandlers.onPeriodMonth);
    this.$eventBus.$on(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_DATES_RANGE, this._eventHandlers.onPeriodDatesRange);
  },
  beforeDestroy() {
    this.$eventBus.$off(PRODUCTS_STATS_EVENTS.PRODUCTS_STATS_AMOUNT, this._eventHandlers.onStatsAmount);
    this.$eventBus.$off(PRODUCTS_STATS_EVENTS.PRODUCTS_STATS_COUNT, this._eventHandlers.onStatsCount);
    this.$eventBus.$off(PRODUCTS_STATS_EVENTS.PRODUCTS_STATS_EARNINGS, this._eventHandlers.onStatsEarnings);
    this.$eventBus.$off(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_ALL, this._eventHandlers.onProductAll);
    this.$eventBus.$off(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_OSAGO, this._eventHandlers.onProductOsago);
    this.$eventBus.$off(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_GREEN_CARD, this._eventHandlers.onProductGreenCard);
    this.$eventBus.$off(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_TOURISM, this._eventHandlers.onProductTourism);
    this.$eventBus.$off(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_HOME, this._eventHandlers.onProductHome);
    this.$eventBus.$off(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_EXTERNAL, this._eventHandlers.onProductExternal);
    this.$eventBus.$off(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_MINI_KASKO, this._eventHandlers.onProductMiniKasko);
    this.$eventBus.$off(PRODUCTS_PRODUCT_FILTER_EVENTS.PRODUCTS_PRODUCT_FILTER_PETS, this._eventHandlers.onProductPets);
    this.$eventBus.$off(PRODUCTS_PARTNER_FILTER_EVENTS.PRODUCTS_PARTNER_FILTER_ALL, this._eventHandlers.onPartnerAll);
    this.$eventBus.$off(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_CURRENT_MONTH, this._eventHandlers.onPeriodCurrentMonth);
    this.$eventBus.$off(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_DAY, this._eventHandlers.onPeriodDay);
    this.$eventBus.$off(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_WEEK, this._eventHandlers.onPeriodWeek);
    this.$eventBus.$off(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_MONTH, this._eventHandlers.onPeriodMonth);
    this.$eventBus.$off(PRODUCTS_PERIOD_FILTER_EVENTS.PRODUCTS_PERIOD_FILTER_DATES_RANGE, this._eventHandlers.onPeriodDatesRange);
  },
  mixins: [ calendarConfig, filterCalculator, productsPartnerSelfFilter, productsPartnerFilter, expirationText,
    balanceFormatter ],
  components: { SlideTitle, SlideFilters, Slide, Chart, DonutChartSkeleton, EmptyChart },
  computed: {
    ...mapGetters('statisticSummaryModule', ['getCurrentMonthValue']),
    ...mapGetters('statisticProductsModule', [
      'getStatisticChartOptions', 'isProductsDataReady', 'isChartEmpty', 'getEmptyProductsChartMessage', 'getActiveFilters',
    ]),
    ...mapGetters('currentUserModule', ['isMainPartner', 'isBankPartner']),
    getCurrentStatisticType() { return productsStatisticTypes.find(type => type.value === this.selectedStatisticType); },
    getCurrentStatisticTypeLabel() { return this.getCurrentStatisticType?.label || ''; },
    getCurrentStatisticTypeDescription() {
      const currentStatisticType = this.getCurrentStatisticType;
      const value = this.getCurrentMonthValue(this.selectedStatisticType);
      const description = currentStatisticType?.description || '';

      if (currentStatisticType?.expiration_forms) {
        return description + ' ' + this.getExpirationText(value, currentStatisticType?.expiration_forms);
      } else {
        return description + ' ' + this.formatBalance(value, '', '₴');
      }
    },
    getCurrentChartOptions() { return this.getStatisticChartOptions(this.selectedStatisticType); },
    getSelectedProductFilter() { return this.getActiveFilters?.product || null; },
    getSelectedPartnersFilter() { return this.getActiveFilters?.partner || null; },
    getSelectedPeriodMode() { return this.getActiveFilters?.periodMode || null; },
  },
  methods: {
    ...mapActions('statisticProductsModule', ['fetchProductsStatistics', 'setupFilter', 'setupPeriodMode']),
    setSelectedStatisticType(type) {
      if (this.selectedStatisticType !== type) {
        this.selectedStatisticType = type;
      }

      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
    setSelectedFilter(type, value, multiselect = false, refetchData = true) {
      this.setupFilter({
        filters: this.calculateFilterValues(this.getActiveFilters, type, value, multiselect),
        mode: 'update',
        refetchData
      });
      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
    setPeriodMode(mode) {
      if (this.getSelectedPeriodMode !== mode) {
        this.setupPeriodMode(mode);
      }
    },
  },
  watch: {
    isProductsDataReady: {
      immediate: false,
      handler(isReady) {
        if (isReady) {
          this.$emit('update-slider');
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.products-slide {
  min-height: 780px;
  padding: 24px 24px 65px;

  @media (max-width: 450px) {
    padding: 24px 24px 120px;
  }
}

.products-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;

  @media (max-width: 1050px) {
    flex-direction: column;
    align-items: start;
  }
}

.products-description {
  font-size: 14px;
  font-weight: 400;
  color: #71717A;
}

.orders-content {
  margin-bottom: 20px;
}

.message-title {
  font-weight: 600;
  margin-top: 60px;
  text-align: center;
}

.chart-skeleton {
  height: 600px;
}

@media (max-width: 1050px) {
  .products-title {
    margin-bottom: 16px;
  }
}

@media (max-width: 550px) {
  .empty-products-chart {
    justify-content: flex-start !important;
  }
}
</style>
